import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { CanvasElementId } from '@blaze-runtime/engine-domain';
import {
  FormListErrors,
  FormValidationErrors,
  UpdateFormRowStatePayload,
} from '@blaze-shared/elements-domain';

/**
 * Writes a formlike container's runtime state — validity and the submission lifecycle —
 * into the store. It is the only place the `updateForm*` actions are dispatched from, so
 * the shape of that state stays in one file.
 */
@Injectable({ providedIn: 'root' })
export class FormStateService {
  private readonly store = inject(Store);

  /** Publishes the validation outcome of a whole form (or of a form list's rows). */
  updateValidity(
    formElementId: CanvasElementId,
    errors: Dictionary<FormValidationErrors> | Dictionary<FormListErrors>,
    blank: boolean,
  ): void {
    const formState = { errors, valid: hasNoErrors(errors), blank };
    this.store.dispatch(runtimeActions.updateFormState({ formElementId, formState }));
  }

  /** Publishes the validation outcome of a single form-list row. */
  updateRowValidity(rowState: UpdateFormRowStatePayload): void {
    this.store.dispatch(runtimeActions.updateFormRowState(rowState));
  }

  /** Marks the form as submitted, which is what makes its errors visible. */
  setSubmitted(formElementId: CanvasElementId): void {
    this.store.dispatch(runtimeActions.updateFormSubmitted({ formElementId }));
  }

  /** Marks a submission as in flight, so the commit button cannot fire twice. */
  setPosted(formElementId: CanvasElementId): void {
    this.store.dispatch(runtimeActions.updateFormPosted({ formElementId }));
  }

  clear(formElementId: CanvasElementId): void {
    this.store.dispatch(runtimeActions.clearFormState({ formElementId }));
  }

  reset(formElementId: CanvasElementId): void {
    this.store.dispatch(runtimeActions.updateFormReset({ formElementId }));
  }
}

/**
 * A form is valid when nothing reported an error. Per-row entries carry their own `valid`
 * flag; per-column entries are error objects, whose mere presence means invalid.
 */
function hasNoErrors(
  errors: Dictionary<FormValidationErrors> | Dictionary<FormListErrors>,
): boolean {
  return (
    !Object.keys(errors).length ||
    Object.entries(errors).reduce(
      (result, [, entry]) => result && ('valid' in entry ? entry.valid : !entry),
      true,
    )
  );
}
